import { BankDetailsUpdateDto, type BankDetailsUpdateInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank.details.update.dto';
import { PersonFormDto, type PersonFormInterface } from '@zaoblako/shared/modules/client/dtos/person/person-form.dto';
import { TaxationTypeEnum } from '@zaoblako/shared/modules/catalog/enums/taxation-type.enum';
import { TaxationTypeEnumNameI18n } from '@zaoblako/shared/modules/catalog/i18n/taxation-type-enum-name.i18n';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { entityLikeSchema, entityLikeSchemaUuid } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ClientDtoKeysEnum } from '@zaoblako/shared/modules/client/enums/client-dto-keys.enum';
import { IntegrationCheckersEnum } from '@zaoblako/shared/modules/integration/enums/integration-checkers.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { StorageBackendValidatorsEnum } from '@zaoblako/shared/modules/storage/enums/storage-backend-validators.enum';
import { uuidSchema } from '@zaoblako/shared/modules/serde/schemas/uuid.schema';
import type { Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import type { CompanyGroupFindInterface } from '@zaoblako/shared/modules/client/dtos/company.group/company-group-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';

import * as yup from 'yup';

/** Common person (company or counterparty) create or update request body */
export interface CompanyFormInterface extends PersonFormInterface {
  connection?: Connection1cFindInterface;
  stamp?: FileFindInterface;
  signature?: FileFindInterface;
  taxationType?: TaxationTypeEnum;
  group?: CompanyGroupFindInterface;
  allDocumentTypesAllowed?: boolean;
  bankDetails: BankDetailsUpdateInterface[];
  currencies?: CurrencyFindInterface[];
  modulbankApiKey?: string;
  alfabankApiKey?: string;
  tbankApiKey?: string;
  tochkabankApiKey?: string;
}

/** Common person (company or counterparty) create or update request body - DTO schema */
export const CompanyFormDto = SerdeDtoClass.from<PersonFormInterface, CompanyFormInterface>(PersonFormDto, ClientDtoKeysEnum.COMPANY_FORM_DTO, {
  connection: {
    optional: true,
    label: 'Соединение с 1С',
    serializer: entityLikeSchema,
    checkers: [IntegrationCheckersEnum.CONNECTION_1C_ASSERT_EXISTS_BY_DTO],
  },
  stamp: {
    optional: true,
    label: 'Печать',
    serializer: entityLikeSchemaUuid,
    checkers: [StorageBackendValidatorsEnum.FILE_ASSERT_EXISTS_BY_DTO],
  },
  signature: {
    optional: true,
    label: 'Подпись',
    serializer: entityLikeSchemaUuid,
    checkers: [StorageBackendValidatorsEnum.FILE_ASSERT_EXISTS_BY_DTO],
  },
  taxationType: {
    optional: true,
    label: 'Тип налогообложения',
    serializer: commonEnumValidator(TaxationTypeEnum, TaxationTypeEnumNameI18n),
  },
  group: {
    optional: true,
    label: 'Группа',
    serializer: entityLikeSchema,
  },
  allDocumentTypesAllowed: {
    optional: true,
    label: 'Изменение всех типов документов',
    serializer: yup.boolean(),
  },
  bankDetails: {
    label: 'Расчётные счета',
    nested: BankDetailsUpdateDto,
    serializer: (_, nested) => yup.array(nested),
  },
  currencies: {
    optional: true,
    label: 'Валюты',
    hint: 'Сейчас параметр необязательный: если массив не задан или пуст, то автоматически выберется Российский Рубль. Потом это изменится.',
    checkers: [CatalogCheckersEnum.CURRENCY_ASSERT_EXISTS_BY_DTO],
    serializer: yup
      .array(yup.object({
        id: uuidSchema,
        isDefault: yup.boolean().notRequired(),
      }))
      .test({
        message: 'Должна быть выбрана одна валюта по-умолчанию',
        test: (value) => {
          if (!value?.length) {
            return true;
          }
          return value.filter(item => item.isDefault)?.length === 1;
        },
      }) as yup.Schema<any>,
  },
  modulbankApiKey: {
    optional: true,
    label: 'API ключ Modulbank',
    serializer: yup.string(),
  },
  alfabankApiKey: {
    optional: true,
    label: 'API ключ Alfabank',
    serializer: yup.string(),
  },
  tbankApiKey: {
    optional: true,
    label: 'API ключ Tbank',
    serializer: yup.string(),
  },
  tochkabankApiKey: {
    optional: true,
    label: 'API ключ Точка Банк',
    serializer: yup.string(),
  },
});
